import React from 'react';
import { cx } from './cx';
import { playClickSound } from '@/utils/soundUtils';

export type UiButtonVariant =
  | 'moneyIn'    // primary magenta — deposits / add funds
  | 'moneyOut'   // primary gold — withdraw / payout
  | 'beam'       // cyan → magenta — progress / chips / accents
  | 'play'       // green — launch game (distinct from moneyIn / moneyOut)
  | 'success'    // primary green — play / confirm actions
  | 'secondary'  // cyan outline — info / nav actions
  | 'tertiary'   // glass
  | 'quiet'      // text link
  | 'destructive';

export type UiButtonSize = 'lg' | 'md' | 'sm' | 'xs';

export interface UiButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: UiButtonVariant;
  size?: UiButtonSize;
  /** Neon glow shadow (Cosmos default: on for money buttons) */
  glow?: boolean;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  /** Skip automatic click sound (rare — prefer default on). */
  silent?: boolean;
}

const VARIANT_CLASSES: Record<UiButtonVariant, string> = {
  moneyIn:
    'bg-gradient-to-b from-money-in to-money-in-deep text-white border-0 hover:brightness-110',
  moneyOut:
    'bg-gradient-to-b from-money-out-soft to-money-out text-on-money-out border-0 hover:brightness-105',
  beam:
    'cosmos-beam text-white border-0 hover:brightness-110',
  play:
    'bg-gradient-to-b from-emerald-500 to-emerald-700 text-white border-0 hover:brightness-110',
  success:
    'bg-gradient-to-b from-emerald-300 to-teal-600 text-emerald-950 border-0 hover:brightness-105',
  secondary:
    'bg-transparent text-info-soft border border-info/60 hover:bg-info/10 hover:border-info',
  tertiary:
    'bg-white/5 text-ink border border-border-ice backdrop-blur-sm hover:bg-white/10',
  quiet:
    'bg-transparent text-ice border-0 underline underline-offset-4 decoration-ice/40 hover:text-money-out-soft',
  destructive:
    'bg-gradient-to-b from-danger to-danger-deep text-white border-0 hover:brightness-110',
};

const GLOW_CLASSES: Partial<Record<UiButtonVariant, string>> = {
  moneyIn: 'shadow-glow-magenta',
  moneyOut: 'shadow-glow-gold',
  beam: 'shadow-glow-beam',
  play: 'shadow-glow-play',
};

const SIZE_CLASSES: Record<UiButtonSize, string> = {
  // Atoms button sizes: L 13.5 / M 11.5 / S 10.5
  lg: 'h-12 px-6 text-[length:var(--text-button-lg)] rounded-control-lg',
  md: 'h-10 px-5 text-[length:var(--text-button)] rounded-control',
  sm: 'h-9 px-4 text-[length:var(--text-button-sm)] rounded-control-sm',
  xs: 'h-7 min-h-7 px-2.5 text-[length:var(--text-button-sm)] rounded-control-sm',
};

/** Cosmos Atoms button (1c). Uppercase, tracked label; magenta = money in, gold = money out. */
export default function Button({
  variant = 'tertiary',
  size = 'md',
  glow = true,
  fullWidth,
  icon,
  className,
  children,
  disabled,
  silent = false,
  onClick,
  ...rest
}: UiButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => {
        if (!disabled && !silent) playClickSound();
        onClick?.(e);
      }}
      className={cx(
        'inline-flex items-center justify-center gap-2 font-ui font-semibold uppercase tracking-[0.1em]',
        'transition-all duration-150 select-none whitespace-nowrap',
        'focus-visible:outline-none focus-visible:shadow-focus-ring',
        'active:translate-y-px',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        glow && !disabled ? GLOW_CLASSES[variant] : undefined,
        fullWidth && 'w-full',
        disabled && 'opacity-40 cursor-not-allowed saturate-50 shadow-none',
        className
      )}
      {...rest}
    >
      {icon && <span className="inline-flex items-center [&>svg]:w-4 [&>svg]:h-4">{icon}</span>}
      {children}
    </button>
  );
}
